import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

// ─── Constants ────────────────────────────────────────────────────────────────

const ALERT_TYPES = [
  {
    key: 'property_alerts',
    label: 'Alertes propriétés',
    description: 'Nouvelles propriétés correspondant à vos critères de recherche',
    icon: 'Home',
    iconBg: 'bg-brand-earth/10',
    iconColor: 'text-brand-earth',
    accentColor: 'brand-earth',
  },
  {
    key: 'appointment_reminders',
    label: 'Rappels de rendez-vous',
    description: 'Rappels avant vos visites, rendez-vous notaire et consultations',
    icon: 'CalendarCheck',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    accentColor: 'blue-600',
  },
  {
    key: 'price_changes',
    label: 'Changements de prix',
    description: 'Baisses ou hausses de prix sur les propriétés que vous suivez',
    icon: 'TrendingDown',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    accentColor: 'amber-600',
  },
  {
    key: 'market_updates',
    label: 'Actualités du marché',
    description: 'Tendances, analyses et rapports du marché immobilier ivoirien',
    icon: 'BarChart2',
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    accentColor: 'purple-600',
  },
  {
    key: 'promotions',
    label: 'Offres & promotions',
    description: 'Offres spéciales, événements et opportunités d\'investissement',
    icon: 'Tag',
    iconBg: 'bg-rose-50',
    iconColor: 'text-rose-500',
    accentColor: 'rose-500',
  },
  {
    key: 'security_alerts',
    label: 'Alertes de sécurité',
    description: 'Connexions suspectes, nouveaux appareils et activités inhabituelles',
    icon: 'ShieldAlert',
    iconBg: 'bg-red-50',
    iconColor: 'text-red-600',
    accentColor: 'red-600',
  },
  {
    key: 'transactional',
    label: 'Messages transactionnels',
    description: 'Confirmations de réservation, codes de vérification et reçus',
    icon: 'Receipt',
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-600',
    accentColor: 'slate-600',
    mandatory: true,
  },
];

const FREQ_OPTIONS = [
  { value: 'immediate', label: 'Immédiat', icon: 'Zap', description: 'Dès que disponible' },
  { value: 'daily', label: 'Quotidien', icon: 'Sun', description: 'Résumé chaque matin' },
  { value: 'weekly', label: 'Hebdomadaire', icon: 'CalendarDays', description: 'Résumé chaque lundi' },
  { value: 'never', label: 'Jamais', icon: 'BellOff', description: 'Désactiver ce type' },
];

const CHANNELS = [
  { key: 'email', label: 'Email', icon: 'Mail', color: 'text-blue-600', bg: 'bg-blue-50', activeBg: 'bg-blue-600' },
  { key: 'sms', label: 'SMS', icon: 'MessageCircle', color: 'text-green-600', bg: 'bg-green-50', activeBg: 'bg-green-600' },
  { key: 'in_app', label: 'In-app', icon: 'Bell', color: 'text-brand-earth', bg: 'bg-brand-earth/10', activeBg: 'bg-brand-earth' },
];

const DEFAULT_ALERT_PREFS = ALERT_TYPES?.reduce((acc, type) => {
  acc[type.key] = {
    enabled: type?.key !== 'promotions',
    frequency: type?.key === 'security_alerts' || type?.key === 'transactional' ? 'immediate' : 'immediate',
    channels: {
      email: true,
      sms: type?.key === 'appointment_reminders' || type?.key === 'security_alerts',
      in_app: true,
    },
  };
  return acc;
}, {});

// ─── Sub-components ───────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange, disabled = false, size = 'md' }) => {
  const w = size === 'sm' ? 'w-9 h-5' : 'w-11 h-6';
  const knob = size === 'sm' ? 'w-4 h-4 top-0.5 left-0.5' : 'w-5 h-5 top-0.5 left-0.5';
  const translate = size === 'sm' ? 'translate-x-4' : 'translate-x-5';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-1 ${w} ${
        checked ? 'bg-brand-earth' : 'bg-border'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      <span
        className={`pointer-events-none absolute inline-block transform rounded-full bg-white shadow-ios-light ring-0 transition duration-200 ease-in-out ${knob} ${
          checked ? translate : 'translate-x-0'
        }`}
      />
    </button>
  );
};

const SectionCard = ({ icon, iconBg = 'bg-brand-earth/10', iconColor = 'text-brand-earth', title, subtitle, children, action = null }) => (
  <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
      <div className="flex items-center space-x-3">
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon name={icon} size={18} className={iconColor} />
        </div>
        <div>
          <p className="text-ios-callout font-body-semibold text-text-primary">{title}</p>
          {subtitle && <p className="text-ios-caption1 text-text-secondary mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
    <div className="px-5 py-5">{children}</div>
  </div>
);

const SaveButton = ({ saving, saved, onClick }) => (
  <div className="flex items-center justify-end space-x-3 pt-2">
    {saved && (
      <span className="flex items-center space-x-1.5 text-ios-caption1 text-green-600 bg-green-50 px-3 py-1.5 rounded-xl border border-green-100">
        <Icon name="CheckCircle" size={13} />
        <span>Préférences sauvegardées</span>
      </span>
    )}
    <button
      onClick={onClick}
      disabled={saving}
      className="flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all duration-200 shadow-ios-light"
    >
      {saving ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>Sauvegarde…</span>
        </>
      ) : (
        <>
          <Icon name="Save" size={15} color="white" />
          <span>Sauvegarder</span>
        </>
      )}
    </button>
  </div>
);

// ─── Alert Type Row ───────────────────────────────────────────────────────────

const AlertTypeRow = ({ type, prefs, onChange }) => {
  const typePrefs = prefs?.[type?.key] || DEFAULT_ALERT_PREFS?.[type?.key];
  const [expanded, setExpanded] = useState(false);

  const handleToggleEnabled = (val) => {
    if (type?.mandatory) return;
    onChange(type?.key, { ...typePrefs, enabled: val });
  };

  const handleFrequency = (freq) => {
    onChange(type?.key, { ...typePrefs, frequency: freq });
  };

  const handleChannel = (channelKey, val) => {
    onChange(type?.key, {
      ...typePrefs,
      channels: { ...typePrefs?.channels, [channelKey]: val },
    });
  };

  const activeChannels = CHANNELS?.filter(c => typePrefs?.channels?.[c?.key]);

  return (
    <div className={`rounded-xl border transition-all duration-200 ${typePrefs?.enabled ? 'bg-white border-border/50' : 'bg-surface/60 border-border/30'}`}>
      {/* Header row */}
      <div className="flex items-start gap-3 p-4">
        <div className={`w-9 h-9 rounded-xl ${type?.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <Icon name={type?.icon} size={17} className={type?.iconColor} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-ios-callout font-body-semibold ${typePrefs?.enabled ? 'text-text-primary' : 'text-text-secondary'}`}>
              {type?.label}
            </p>
            {type?.mandatory && (
              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-ios-caption2 text-slate-500 font-body-medium">
                Obligatoire
              </span>
            )}
          </div>
          <p className="text-ios-caption1 text-text-secondary mt-0.5 leading-relaxed">{type?.description}</p>

          {/* Channel badges */}
          {typePrefs?.enabled && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {activeChannels?.map(c => (
                <span key={c?.key} className={`flex items-center space-x-1 px-2 py-0.5 rounded-full ${c?.bg} text-ios-caption2 font-body-medium ${c?.color}`}>
                  <Icon name={c?.icon} size={10} />
                  <span>{c?.label}</span>
                </span>
              ))}
              {activeChannels?.length === 0 && (
                <span className="text-ios-caption2 text-text-secondary italic">Aucun canal actif</span>
              )}
              <span className="text-ios-caption2 text-text-secondary">·</span>
              <span className="text-ios-caption2 text-text-secondary">
                {FREQ_OPTIONS?.find(f => f?.value === typePrefs?.frequency)?.label || 'Immédiat'}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {typePrefs?.enabled && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="w-7 h-7 rounded-lg bg-surface hover:bg-border/40 flex items-center justify-center transition-colors"
              title={expanded ? 'Réduire' : 'Configurer'}
            >
              <Icon name={expanded ? 'ChevronUp' : 'Settings2'} size={14} className="text-text-secondary" />
            </button>
          )}
          <Toggle
            checked={typePrefs?.enabled}
            onChange={handleToggleEnabled}
            disabled={type?.mandatory}
          />
        </div>
      </div>
      {/* Expanded config */}
      {typePrefs?.enabled && expanded && (
        <div className="border-t border-border/30 px-4 pb-4 pt-3 space-y-4">
          {/* Delivery channels */}
          <div>
            <p className="text-ios-caption1 font-body-semibold text-text-secondary uppercase tracking-wide mb-2.5">
              Canaux de livraison
            </p>
            <div className="grid grid-cols-3 gap-2">
              {CHANNELS?.map(channel => {
                const active = typePrefs?.channels?.[channel?.key];
                return (
                  <button
                    key={channel?.key}
                    type="button"
                    onClick={() => handleChannel(channel?.key, !active)}
                    className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border transition-all duration-150 ${
                      active
                        ? `${channel?.bg} border-current ${channel?.color} shadow-ios-light`
                        : 'bg-surface border-border/50 text-text-secondary hover:border-border'
                    }`}
                  >
                    <Icon name={channel?.icon} size={16} className={active ? channel?.color : 'text-text-secondary'} />
                    <span className={`text-ios-caption2 font-body-medium ${active ? channel?.color : 'text-text-secondary'}`}>
                      {channel?.label}
                    </span>
                    {active && (
                      <div className={`w-1.5 h-1.5 rounded-full ${channel?.activeBg}`} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Frequency threshold */}
          {!type?.mandatory && (
            <div>
              <p className="text-ios-caption1 font-body-semibold text-text-secondary uppercase tracking-wide mb-2.5">
                Seuil de fréquence
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {FREQ_OPTIONS?.map(opt => (
                  <button
                    key={opt?.value}
                    type="button"
                    onClick={() => handleFrequency(opt?.value)}
                    className={`flex flex-col items-start p-3 rounded-xl border transition-all duration-150 ${
                      typePrefs?.frequency === opt?.value
                        ? 'bg-brand-earth/5 border-brand-earth ring-1 ring-brand-earth/20' :'bg-surface border-border/50 hover:border-brand-earth/30'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-1.5 ${
                      typePrefs?.frequency === opt?.value ? 'bg-brand-earth/15' : 'bg-white border border-border/60'
                    }`}>
                      <Icon name={opt?.icon} size={14} className={typePrefs?.frequency === opt?.value ? 'text-brand-earth' : 'text-text-secondary'} />
                    </div>
                    <p className={`text-ios-caption1 font-body-semibold leading-tight ${
                      typePrefs?.frequency === opt?.value ? 'text-brand-earth' : 'text-text-primary'
                    }`}>
                      {opt?.label}
                    </p>
                    <p className="text-ios-caption2 text-text-secondary mt-0.5 leading-tight">{opt?.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Global Channel Override ──────────────────────────────────────────────────

const GlobalChannelCard = ({ globalChannels, onChange }) => (
  <SectionCard
    icon="ToggleRight"
    iconBg="bg-slate-100"
    iconColor="text-slate-600"
    title="Canaux globaux"
    subtitle="Activez ou désactivez un canal pour toutes les alertes"
  >
    <div className="space-y-3">
      {CHANNELS?.map(channel => (
        <div key={channel?.key} className="flex items-center justify-between py-3 px-4 rounded-xl bg-surface border border-border/40">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-xl ${channel?.bg} flex items-center justify-center`}>
              <Icon name={channel?.icon} size={16} className={channel?.color} />
            </div>
            <div>
              <p className="text-ios-callout font-body-medium text-text-primary">{channel?.label}</p>
              <p className="text-ios-caption1 text-text-secondary">
                {channel?.key === 'email' && 'Notifications par email'}
                {channel?.key === 'sms' && 'Notifications par SMS'}
                {channel?.key === 'in_app' && 'Notifications dans l\'application'}
              </p>
            </div>
          </div>
          <Toggle
            checked={globalChannels?.[channel?.key] !== false}
            onChange={val => onChange(channel?.key, val)}
          />
        </div>
      ))}
    </div>
  </SectionCard>
);

// ─── Quiet Hours ──────────────────────────────────────────────────────────────

const QuietHoursCard = ({ quietHours, onChange }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const fmt = h => `${String(h)?.padStart(2, '0')}:00`;

  return (
    <SectionCard
      icon="MoonStar"
      iconBg="bg-indigo-50"
      iconColor="text-indigo-600"
      title="Heures silencieuses"
      subtitle="Aucune notification SMS/in-app pendant cette plage horaire"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-surface border border-border/40">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center">
              <Icon name="Moon" size={16} className="text-indigo-600" />
            </div>
            <div>
              <p className="text-ios-callout font-body-medium text-text-primary">Activer les heures silencieuses</p>
              <p className="text-ios-caption1 text-text-secondary">Suspendre les alertes non urgentes</p>
            </div>
          </div>
          <Toggle
            checked={quietHours?.enabled}
            onChange={val => onChange({ ...quietHours, enabled: val })}
          />
        </div>

        {quietHours?.enabled && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1.5">Début</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Icon name="Moon" size={14} className="text-text-secondary" />
                </div>
                <select
                  value={quietHours?.start ?? 22}
                  onChange={e => onChange({ ...quietHours, start: parseInt(e?.target?.value) })}
                  className="w-full pl-9 pr-3 py-2.5 border border-border/60 rounded-xl text-ios-callout text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold/60"
                >
                  {hours?.map(h => <option key={h} value={h}>{fmt(h)}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1.5">Fin</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Icon name="Sun" size={14} className="text-text-secondary" />
                </div>
                <select
                  value={quietHours?.end ?? 8}
                  onChange={e => onChange({ ...quietHours, end: parseInt(e?.target?.value) })}
                  className="w-full pl-9 pr-3 py-2.5 border border-border/60 rounded-xl text-ios-callout text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold/60"
                >
                  {hours?.map(h => <option key={h} value={h}>{fmt(h)}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AlertPreferencesPanel = () => {
  const { user } = useAuth();

  const [alertPrefs, setAlertPrefs] = useState(DEFAULT_ALERT_PREFS);
  const [globalChannels, setGlobalChannels] = useState({ email: true, sms: true, in_app: true });
  const [quietHours, setQuietHours] = useState({ enabled: false, start: 22, end: 8 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const loadPrefs = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error: fetchErr } = await supabase
        ?.from('alert_preferences')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();

      if (!fetchErr && data) {
        if (data?.alert_types) setAlertPrefs(data?.alert_types);
        if (data?.global_channels) setGlobalChannels(data?.global_channels);
        if (data?.quiet_hours) setQuietHours(data?.quiet_hours);
      }
    } catch (err) {
      console.error('Error loading alert preferences:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const handleAlertTypeChange = (typeKey, newPrefs) => {
    setAlertPrefs(prev => ({ ...prev, [typeKey]: newPrefs }));
  };

  const handleGlobalChannel = (channelKey, val) => {
    setGlobalChannels(prev => ({ ...prev, [channelKey]: val }));
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setError('');
    setSaving(true);
    try {
      const { error: upsertErr } = await supabase
        ?.from('alert_preferences')
        ?.upsert({
          user_id: user?.id,
          alert_types: alertPrefs,
          global_channels: globalChannels,
          quiet_hours: quietHours,
          updated_at: new Date()?.toISOString(),
        }, { onConflict: 'user_id' });

      if (upsertErr) {
        // Fallback: store locally
        localStorage.setItem(`alert_prefs_${user?.id}`, JSON.stringify({ alertPrefs, globalChannels, quietHours }));
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Une erreur est survenue lors de la sauvegarde.');
      console.error('Error saving alert preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = Object.values(alertPrefs)?.filter(p => p?.enabled)?.length;

  if (!user) {
    return (
      <div className="min-h-screen bg-surface/30">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-brand-earth/10 flex items-center justify-center mx-auto mb-5">
            <Icon name="Lock" size={28} className="text-brand-earth" />
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-3">
            Connexion requise
          </h2>
          <p className="text-ios-callout text-text-secondary mb-6">
            Connectez-vous pour gérer vos préférences d'alertes.
          </p>
          <Link
            to="/auth"
            className="inline-flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-colors"
          >
            <Icon name="LogIn" size={16} color="white" />
            <span>Se connecter</span>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Préférences d'Alertes – Kapital States</title>
        <meta name="description" content="Contrôlez les types d'alertes reçues, les canaux de livraison (email, SMS, in-app) et les seuils de fréquence." />
      </Helmet>
      <div className="min-h-screen bg-surface/30">
        <Header />

        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-ios-caption1 text-text-secondary mb-6 flex-wrap gap-y-1">
            <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
            <Icon name="ChevronRight" size={12} />
            <Link to="/account-dashboard" className="hover:text-brand-earth transition-colors">Mon Compte</Link>
            <Icon name="ChevronRight" size={12} />
            <span className="text-text-primary font-body-medium">Préférences d'alertes</span>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-gold/80 to-brand-earth flex items-center justify-center shadow-ios-light flex-shrink-0">
                  <Icon name="SlidersHorizontal" size={26} color="white" />
                </div>
                <div>
                  <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth leading-tight">
                    Préférences d'alertes
                  </h1>
                  <p className="text-ios-callout text-text-secondary mt-0.5">
                    Contrôlez les types, canaux et fréquences de vos alertes
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <div className="px-3 py-1.5 rounded-xl bg-brand-earth/10 border border-brand-earth/20">
                  <span className="text-ios-caption1 font-body-semibold text-brand-earth">
                    {enabledCount}/{ALERT_TYPES?.length} actives
                  </span>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">

              {/* ── Global Channels ── */}
              <GlobalChannelCard globalChannels={globalChannels} onChange={handleGlobalChannel} />

              {/* ── Alert Types ── */}
              <SectionCard
                icon="BellRing"
                iconBg="bg-brand-earth/10"
                iconColor="text-brand-earth"
                title="Types d'alertes"
                subtitle="Activez les alertes et configurez les canaux et fréquences par type"
              >
                <div className="space-y-3">
                  {ALERT_TYPES?.map(type => (
                    <AlertTypeRow
                      key={type?.key}
                      type={type}
                      prefs={alertPrefs}
                      onChange={handleAlertTypeChange}
                    />
                  ))}
                </div>
              </SectionCard>

              {/* ── Quiet Hours ── */}
              <QuietHoursCard quietHours={quietHours} onChange={setQuietHours} />

              {/* Error */}
              {error && (
                <div className="flex items-center space-x-2 text-ios-caption1 text-red-600 bg-red-50 px-4 py-3 rounded-xl border border-red-100">
                  <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Save */}
              <SaveButton saving={saving} saved={saved} onClick={handleSave} />

              {/* Info note */}
              <div className="flex items-start space-x-3 p-4 rounded-2xl bg-blue-50 border border-blue-100">
                <Icon name="Info" size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-ios-caption1 text-blue-700 leading-relaxed">
                  Les alertes de sécurité et messages transactionnels essentiels (codes de vérification, confirmations de paiement) sont toujours envoyés, conformément à nos obligations légales.
                </p>
              </div>

              {/* Quick links */}
              <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 px-5 py-4">
                <p className="text-ios-footnote font-body-semibold text-text-secondary mb-3 uppercase tracking-wide">Liens rapides</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Link
                    to="/notification-preferences"
                    className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-surface transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-brand-earth/10 flex items-center justify-center group-hover:bg-brand-earth/15 transition-colors">
                      <Icon name="Bell" size={15} className="text-brand-earth" />
                    </div>
                    <div>
                      <p className="text-ios-callout font-body-medium text-text-primary">Préférences SMS & Email</p>
                      <p className="text-ios-caption1 text-text-secondary">Fréquence et numéro de téléphone</p>
                    </div>
                    <Icon name="ChevronRight" size={14} className="text-text-secondary ml-auto" />
                  </Link>
                  <Link
                    to="/account-communication-settings"
                    className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-surface transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
                      <Icon name="MessageSquare" size={15} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-ios-callout font-body-medium text-text-primary">Paramètres de communication</p>
                      <p className="text-ios-caption1 text-text-secondary">Alertes propriétés et canaux</p>
                    </div>
                    <Icon name="ChevronRight" size={14} className="text-text-secondary ml-auto" />
                  </Link>
                </div>
              </div>

            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default AlertPreferencesPanel;
